import { withCurrencyCode } from '@platewell/schema';
import { useEffect, useState } from 'react';
import { annualPackage } from './billing.js';

export interface PlanPrice {
  /**
   * The store's own formatted price for this customer — `$39.99`, `¥5,000`, `39,99 €`.
   *
   * Null while loading, and when no offering could be read. The number is never assembled
   * here from a value and a symbol: RevenueCat returns the string it will actually charge,
   * and anything we format ourselves risks disagreeing with checkout. Only the ISO code is
   * appended, which disambiguates `$` without touching the amount.
   */
  formatted: string | null;
  loading: boolean;
}

/**
 * Reads the annual package's price up front, rather than at the moment of purchase.
 *
 * The offering was already being fetched inside the buy handler, which meant the price
 * was only known once it was too late to show it. Fetching on mount costs one request and
 * lets every price on the screen be the real one.
 */
export function usePlanPrice(): PlanPrice {
  const [formatted, setFormatted] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void annualPackage()
      .then((rcPackage) => {
        if (cancelled) return;
        const price = rcPackage?.webBillingProduct.currentPrice;
        setFormatted(price == null ? null : withCurrencyCode(price.formattedPrice, price.currency));
      })
      .catch(() => {
        // A missing offering is already reported when the user tries to buy; a price that
        // will not load is not worth a second error on the way in.
        if (!cancelled) setFormatted(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { formatted, loading };
}
